/**
 * VerifyLeadPage — Single-lead verification screen.
 *
 * Apr 28, 2026 (Squad-D). Screen 03 of 12.
 *
 * Layout:
 *  - Customer card (priority chip · source · received-when · CTA: Call)
 *  - Left: ChatKart conversation panel + Source data panel
 *  - Right: Verification form panel
 *      Decision (Verified / Rejected / Snooze 2h)
 *      Product picker · Budget slider · Region select · Notes
 *      Verify & assign | Queue in pool
 *
 * Keyboard:
 *  V — verify · R — reject · N — next · Esc — back
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, X, Clock, Phone, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react';
import { api } from '@/utils/api';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import {
  PageShell,
  KbdHint,
  Panel,
  PriorityChip,
  SourceIcon,
  Avatar,
  FieldLabel,
  PRODUCT_OPTIONS,
  REGION_OPTIONS,
  type Lead,
  unwrapList,
  leadName,
  leadPhone,
  leadProduct,
  leadRegion,
  priorityFrom,
  relativeTime,
  sourceMeta,
} from './_lmShared';

interface ChatMessage {
  id?: string;
  from: 'aura' | 'cust' | 'agent' | 'customer' | 'system';
  text?: string;
  body?: string;
  t?: string;
  when?: string;
  read?: boolean;
  createdAt?: string;
}

export function VerifyLeadPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [lead, setLead] = useState<Lead | null>(null);
  const [queue, setQueue] = useState<Lead[]>([]);
  const [transcript, setTranscript] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [decision, setDecision] = useState<'verified' | 'rejected' | 'snoozed' | null>('verified');
  const [product, setProduct] = useState('');
  const [budgetIdx, setBudgetIdx] = useState<[number, number]>([3, 5]); // bracket idx
  const [region, setRegion] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* Fetch this lead. */
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    safeGet<Lead>(`/leads/${id}`).then((r) => {
      const l = r.data;
      setLead(l);
      if (l) {
        setProduct(leadProduct(l) === '—' ? '' : leadProduct(l));
        setRegion(leadRegion(l) === '—' ? '' : leadRegion(l));
        setNotes(l.notes ?? l.note ?? '');
      }
      setLoading(false);
    });

    /* Pull queue for prev/next navigation. */
    safeGet<unknown>('/leads?verificationStatus=pending_verification&limit=100').then((r) => {
      setQueue(unwrapList<Lead>(r.data));
    });

    /* Pull conversation transcript if available. */
    safeGet<unknown>(`/leads/${id}/messages`).then((r) => {
      const list = unwrapList<ChatMessage>(r.data);
      setTranscript(list);
    });
  }, [id]);

  const queuePos = useMemo(() => {
    const i = queue.findIndex((q) => q.id === id);
    return { idx: i, total: queue.length };
  }, [queue, id]);

  function goPrev() {
    const i = queuePos.idx;
    if (i > 0) navigate(`/lead-manager/verify/${queue[i - 1].id}`);
  }

  function goNext() {
    const i = queuePos.idx;
    if (i >= 0 && i < queue.length - 1) navigate(`/lead-manager/verify/${queue[i + 1].id}`);
  }

  async function submit(decisionOverride?: 'verified' | 'rejected' | 'snoozed') {
    if (!id) return;
    const finalDecision = decisionOverride ?? decision;
    if (!finalDecision) return;
    setSubmitting(true);
    try {
      await api.post(`/leads/${id}/verify`, {
        decision: finalDecision,
        product,
        budget: BUDGET_LABEL(budgetIdx),
        region,
        notes,
      });
      // Auto-advance to next unverified.
      goNext();
    } catch (err) {
      alert('Verify failed — backend not yet ready, or you do not have permission.');
    } finally {
      setSubmitting(false);
    }
  }

  /* Keyboard shortcuts: V/R/N/Esc */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'Escape') {
        e.preventDefault();
        navigate('/lead-manager/unverified');
        return;
      }
      if (e.key === 'v' || e.key === 'V') {
        e.preventDefault();
        submit('verified');
        return;
      }
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        submit('rejected');
        return;
      }
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        goNext();
        return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, queuePos.idx, decision, product, budgetIdx, region, notes]);

  if (loading) {
    return (
      <PageShell title={`Verify lead · ${id}`}>
        <div style={{ padding: 28, textAlign: 'center', color: 'var(--ink-3)' }}>Loading lead…</div>
      </PageShell>
    );
  }

  if (!lead) {
    return (
      <PageShell title={`Verify lead · ${id}`}>
        <div style={{ padding: 28, textAlign: 'center', color: 'var(--ink-3)' }}>
          Lead not found. <button className="btn btn-outline btn-sm" onClick={() => navigate('/lead-manager/unverified')}>Back to queue</button>
        </div>
      </PageShell>
    );
  }

  const meta = sourceMeta(lead.source);

  return (
    <PageShell
      title={`Verify lead · ${lead.id.slice(-6)}`}
      sub={
        queuePos.idx >= 0 && queuePos.total > 0
          ? `${queuePos.idx + 1} of ${queuePos.total} in queue · loop runs FIFO`
          : 'Single lead'
      }
      right={
        <>
          <button className="btn btn-outline" onClick={goPrev} disabled={queuePos.idx <= 0}>
            <ChevronLeft size={13} /> Prev
          </button>
          <button
            className="btn btn-outline"
            onClick={goNext}
            disabled={queuePos.idx < 0 || queuePos.idx >= queuePos.total - 1}
          >
            Next <ChevronRight size={13} />
          </button>
          <button className="btn btn-primary">
            <Phone size={13} /> Call to verify
          </button>
        </>
      }
    >
      {/* Customer card */}
      <div
        className="card"
        style={{
          padding: 20,
          marginBottom: 16,
          display: 'flex',
          gap: 20,
          alignItems: 'center',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 14,
            background: 'linear-gradient(135deg, #1B1713, #3A332B)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--ff-serif)',
            fontSize: 26,
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          {(leadName(lead) ?? '?').split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() ?? '').join('') || '?'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
            <PriorityChip p={priorityFrom(lead)} />
            <span className="chip">
              <SourceIcon source={lead.source} size={12} /> {meta.label}
            </span>
            {lead.createdAt && (
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                received {relativeTime(lead.createdAt)} ago
              </span>
            )}
          </div>
          <div
            style={{
              fontFamily: 'var(--ff-serif)',
              fontWeight: 500,
              fontSize: 28,
              letterSpacing: -0.4,
            }}
          >
            {leadName(lead)}
          </div>
          <div style={{ display: 'flex', gap: 18, fontSize: 13, color: 'var(--ink-2)', marginTop: 4, flexWrap: 'wrap' }}>
            <span className="mono">{leadPhone(lead)}</span>
            <span>·</span>
            <span>{leadRegion(lead)}</span>
            <span>·</span>
            <span style={{ color: 'var(--ink-0)', fontWeight: 500 }}>{leadProduct(lead)}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button className="btn btn-primary">
            <Phone size={13} /> Call
          </button>
          <button className="btn btn-outline">
            <MoreVertical size={13} />
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
        {/* Left: transcript + source */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Panel
            title="Conversation"
            sub={transcript.length ? `${transcript.length} messages` : 'No transcript'}
          >
            <ChatTranscript messages={transcript} />
          </Panel>
          <Panel title="Source data" sub={`${meta.label} · ${meta.hint}`}>
            <SourcePayload lead={lead} />
          </Panel>
        </div>

        {/* Right: verification form */}
        <Panel title="Verification" sub="Mark decision after the call">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <FieldLabel>Decision</FieldLabel>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                <DecisionBtn
                  label="Verified"
                  tone="green"
                  active={decision === 'verified'}
                  icon={<Check size={13} />}
                  onClick={() => setDecision('verified')}
                />
                <DecisionBtn
                  label="Rejected"
                  tone="red"
                  active={decision === 'rejected'}
                  icon={<X size={13} />}
                  onClick={() => setDecision('rejected')}
                />
                <DecisionBtn
                  label="Snooze 2h"
                  tone=""
                  active={decision === 'snoozed'}
                  icon={<Clock size={13} />}
                  onClick={() => setDecision('snoozed')}
                />
              </div>
            </div>
            <div>
              <FieldLabel>Product interest</FieldLabel>
              <select
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                style={selectStyle}
              >
                <option value="">Select product…</option>
                {PRODUCT_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
                {/* Allow product values not in defaults (eg from source) */}
                {product && !PRODUCT_OPTIONS.includes(product) && (
                  <option value={product}>{product}</option>
                )}
              </select>
            </div>
            <div>
              <FieldLabel>
                Budget hint <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>(₹)</span>
              </FieldLabel>
              <BudgetSlider value={budgetIdx} onChange={setBudgetIdx} />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: 'var(--ink-3)',
                  marginTop: 4,
                }}
                className="mono"
              >
                <span>₹50K</span>
                <span style={{ color: 'var(--ink-0)', fontWeight: 500 }}>
                  {BUDGET_LABEL(budgetIdx)}
                </span>
                <span>₹50 L</span>
              </div>
            </div>
            <div>
              <FieldLabel>Region</FieldLabel>
              <select value={region} onChange={(e) => setRegion(e.target.value)} style={selectStyle}>
                <option value="">Select region…</option>
                {REGION_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
                {region && !REGION_OPTIONS.includes(region) && (
                  <option value={region}>{region}</option>
                )}
              </select>
            </div>
            <div>
              <FieldLabel>Notes</FieldLabel>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any context the assigned exec should know."
                style={{
                  width: '100%',
                  minHeight: 80,
                  padding: '8px 10px',
                  resize: 'vertical',
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  fontFamily: 'inherit',
                  fontSize: 12,
                  background: 'var(--ivory-0)',
                  color: 'var(--ink-0)',
                  outline: 'none',
                }}
              />
            </div>
            <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, display: 'flex', gap: 6 }}>
              <button
                className="btn btn-primary"
                style={{ flex: 1 }}
                disabled={submitting}
                onClick={() => submit()}
              >
                <Check size={13} /> {submitting ? 'Saving…' : 'Verify & assign now'}
              </button>
              <button
                className="btn btn-outline"
                disabled={submitting}
                onClick={() => submit('verified')}
              >
                Queue in pool
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'center' }}>
              <KbdHint k="V" /> verify · <KbdHint k="R" /> reject · <KbdHint k="N" /> next ·{' '}
              <KbdHint k="Esc" /> back
            </div>
          </div>
        </Panel>
      </div>
    </PageShell>
  );
}

/* ─── Local atoms ──────────────────────────────────────────────────────── */

const selectStyle: React.CSSProperties = {
  width: '100%',
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--line)',
  borderRadius: 6,
  fontFamily: 'inherit',
  fontSize: 13,
  background: 'var(--ivory-0)',
  color: 'var(--ink-0)',
  outline: 'none',
};

function DecisionBtn({
  label,
  tone,
  active,
  icon,
  onClick,
}: {
  label: string;
  tone: '' | 'green' | 'red';
  active: boolean;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  const bg = active
    ? tone === 'green'
      ? 'var(--forest-50)'
      : tone === 'red'
      ? 'var(--danger-bg)'
      : 'var(--ivory-2)'
    : 'var(--ivory-0)';
  const fg = active
    ? tone === 'green'
      ? 'var(--forest-600)'
      : tone === 'red'
      ? 'var(--danger)'
      : 'var(--ink-1)'
    : 'var(--ink-2)';
  const bd = active
    ? tone === 'green'
      ? 'var(--forest-100)'
      : tone === 'red'
      ? '#EFCFCA'
      : 'var(--line)'
    : 'var(--line)';
  return (
    <button
      onClick={onClick}
      className="btn"
      style={{
        background: bg,
        color: fg,
        border: '1px solid ' + bd,
        height: 36,
        justifyContent: 'center',
        fontWeight: 500,
      }}
    >
      {icon} {label}
    </button>
  );
}

const BUDGET_BRACKETS = [
  '<₹50K',
  '₹50K–1L',
  '₹1–2 L',
  '₹2–4 L',
  '₹4–6 L',
  '₹6–10 L',
  '₹10–20 L',
  '₹20–50 L',
];

function BUDGET_LABEL([lo, hi]: [number, number]): string {
  if (lo === hi) return BUDGET_BRACKETS[lo] ?? '—';
  return `${BUDGET_BRACKETS[lo]} – ${BUDGET_BRACKETS[hi]}`;
}

function BudgetSlider({
  value,
  onChange,
}: {
  value: [number, number];
  onChange: (v: [number, number]) => void;
}) {
  const max = BUDGET_BRACKETS.length - 1;
  const [lo, hi] = value;
  return (
    <div style={{ paddingTop: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="range"
          min={0}
          max={max}
          value={lo}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange([Math.min(v, hi), hi]);
          }}
          style={{ flex: 1 }}
        />
        <input
          type="range"
          min={0}
          max={max}
          value={hi}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange([lo, Math.max(v, lo)]);
          }}
          style={{ flex: 1 }}
        />
      </div>
    </div>
  );
}

function ChatTranscript({ messages }: { messages: ChatMessage[] }) {
  if (messages.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>
        No conversation yet. Once the lead engages via WhatsApp / chat, messages will appear here.
      </div>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        maxHeight: 220,
        overflowY: 'auto',
        paddingRight: 4,
      }}
    >
      {messages.map((m, i) => {
        const fromAura = m.from === 'aura' || m.from === 'agent' || m.from === 'system';
        const text = m.text ?? m.body ?? '';
        const when = m.when ?? (m.createdAt ? relativeTime(m.createdAt) + ' ago' : '');
        return (
          <div
            key={m.id ?? i}
            style={{
              alignSelf: fromAura ? 'flex-start' : 'flex-end',
              maxWidth: '85%',
              background: fromAura ? 'var(--ivory-2)' : '#DCF8C6',
              color: 'var(--ink-1)',
              fontSize: 12,
              lineHeight: 1.45,
              padding: '7px 10px',
              borderRadius: 8,
              border: '1px solid ' + (fromAura ? 'var(--line)' : '#C8E5B6'),
            }}
          >
            <div>{text}</div>
            {when && (
              <div
                style={{
                  fontSize: 9,
                  color: 'var(--ink-4)',
                  marginTop: 3,
                  textAlign: 'right',
                }}
              >
                {when}
                {m.read && ' · ✓✓'}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SourcePayload({ lead }: { lead: Lead }) {
  const fields: [string, string][] = [
    ['Lead ID', lead.id],
    ['Source', lead.source ?? '—'],
    ['Phone', leadPhone(lead)],
    ['Email', lead.email ?? '—'],
    ['Product interest', leadProduct(lead)],
    ['Region', leadRegion(lead)],
    ['Conversation state', lead.conversationState ?? '—'],
    ['Created', lead.createdAt ?? '—'],
  ];
  return (
    <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--ink-1)', lineHeight: 1.7 }}>
      {fields.map(([k, v]) => (
        <div
          key={k}
          style={{
            display: 'flex',
            gap: 10,
            borderBottom: '1px dashed var(--ivory-3)',
            padding: '3px 0',
          }}
        >
          <span style={{ width: 130, color: 'var(--ink-3)', flexShrink: 0 }}>{k}</span>
          <span style={{ flex: 1, wordBreak: 'break-word' }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

export default VerifyLeadPage;
